import {
  Avatar,
  Box,
  Button,
  Divider,
  Grid,
  IconButton,
  Menu,
  MenuItem,
  Tooltip,
  Typography
} from "@mui/material";
import { ExitToApp } from "@mui/icons-material";
import React from "react";
import { tokens } from "../../../../theme";
import { onChrome, type ShellChromeProps } from "../shellChrome";
import { LEGACY_NAV_LABELS } from "./legacyScreens";

/**
 * ── LEGACY SHELL — SCHEDULED FOR DELETION ──────────────────────────────────
 *
 * The pre-refresh chrome: green stripe, navy header with greeting and Portal
 * Management, and the grey tab strip. Recovered from commit 2700a79 (and still
 * on `frontend_refactor`) so the app can ship the refactor while looking like
 * the app people already use.
 *
 * It exists to be removed. Everything legacy lives in this folder — see
 * README.md here for the three-step deletion.
 *
 * Written against tokens rather than the original literals, so it obeys
 * invariant 2 and renders correctly under any theme. Against the `legacy`
 * theme those tokens — railBg, railSystemBg, positive.main — hold exactly the
 * original header navy, tab-strip grey and brand green.
 *
 * Not reproduced: "Member Since 2024", which was hard-coded in the original.
 * Showing a fabricated date is worse than showing the real role.
 */
const LegacyBar = (props: ShellChromeProps) => {
  const {
    nav,
    selectedItemId,
    onSelectItem,
    variant,
    onExitSystem,
    user,
    onOpenSystemManagement,
    onOpenSwitcher,
    onLogout
  } = props;

  const [menuAnchor, setMenuAnchor] = React.useState<null | HTMLElement>(null);
  const items = nav.groups.flatMap((g) => g.items);
  const firstName = user?.name?.split(" ")[0] ?? "";

  // One button with one label, as `header.tsx` had — only the handler differs by
  // screen, and `null` reproduces `!mainView &&` hiding it on the selection view.
  //
  // On System Management the original's onClick dispatched `updateMainView()`,
  // which only hid the button and left the avatar menu as the way back to the
  // dashboard. `onExitSystem` is that same journey without the dead click; it is
  // the one deliberate behavioural deviation in this file. Reproducing a no-op
  // would be reproducing a bug.
  //
  // Off System Management, the presence of the handler is the gate rather than
  // `canSwitchContext` — PortalShell withholds it on the selection screen, and
  // gating on `canSwitchContext` alone left a visible button with a dead onClick.
  const portalAction =
    variant === "system" ? onExitSystem : nav.canSwitchContext ? onOpenSwitcher : undefined;

  const portalButton = portalAction ? (
    <Button
      variant="outlined"
      onClick={(e) => (variant === "system" ? onExitSystem?.() : onOpenSwitcher?.(e.currentTarget))}
      sx={{ height: "40px", color: onChrome.strong, gap: 1 }}
    >
      <ExitToApp /> PORTAL MANAGEMENT
    </Button>
  ) : null;

  return (
    <Box component="header" sx={{ flex: "none" }}>
      <Box sx={{ height: 10, bgcolor: tokens.color.positive.main }} />

      {/* Navy on EVERY screen, System Management included. `header.tsx:76` on
          `frontend_refactor` hard-codes #004680 with no variant — the original
          had no "you have left a client context" chrome at all, only the grey
          TAB STRIP that systemManagement.tsx drew for itself. Tinting the
          header grey here also exposed the navy rectangle baked into
          mainLogo.png, which is invisible only against this exact navy. */}
      <Grid
        container
        sx={{
          backgroundColor: tokens.color.railBg,
          padding: "1% 3% 1% 2%"
        }}
      >
        {/* 2 / 5 / 5, as the original — the logo is a background image in an
            80px block, not an inline img, which is what set its scale. */}
        <Grid item xs={2} sx={{ display: "flex", flexDirection: "column", height: "80px" }}>
          <Grid
            item
            xs={12}
            sx={{
              backgroundImage: `url(${require("../../../../assets/mainLogo.png")})`,
              backgroundRepeat: "no-repeat",
              height: "100px"
            }}
          />
        </Grid>

        <Grid
          item
          xs={5}
          sx={{
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            color: onChrome.strong,
            paddingLeft: "20px"
          }}
        >
          <Typography variant="h6">Hello {firstName}!</Typography>
          <Typography sx={{ fontSize: "12px" }}>Welcome back to the dashboard</Typography>
        </Grid>

        <Grid
          container
          item
          xs={5}
          sx={{ display: "flex", justifyContent: "flex-end", alignItems: "center" }}
        >
          {/* 7 / 5 INSIDE the right-hand 5 columns, as the original. The split is
              what spaces these two apart, not a margin: each column right-aligns
              its own content, so whatever the avatar does not use of its 5/12 is
              left as a gap to the button's right. A flat flex row with `ml` put
              them side by side and read visibly tighter. */}
          {portalButton && (
            <Grid
              item
              xs={7}
              sx={{ display: "flex", justifyContent: "flex-end", alignItems: "center" }}
            >
              {portalButton}
            </Grid>
          )}

          <Grid
            item
            xs={5}
            sx={{ display: "flex", justifyContent: "flex-end", alignItems: "center" }}
          >
            <Tooltip title="Open settings">
              <IconButton onClick={(e) => setMenuAnchor(e.currentTarget)} sx={{ p: 0 }}>
                <Grid sx={{ display: "flex" }} gap={1}>
                  <Grid
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      justifyContent: "center",
                      color: onChrome.strong
                    }}
                  >
                    <Avatar
                      sx={{ border: `1px solid ${onChrome.border}`, bgcolor: "transparent" }}
                    >
                      {user?.initial ?? ""}
                    </Avatar>
                  </Grid>
                  <Grid
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "flex-start",
                      color: onChrome.strong
                    }}
                  >
                    <Typography variant="h6">{user?.name ?? ""}</Typography>
                    <Typography sx={{ fontSize: "12px" }}>{user?.roleLabel ?? ""}</Typography>
                  </Grid>
                </Grid>
              </IconButton>
            </Tooltip>
          </Grid>

          <Menu
            anchorEl={menuAnchor}
            open={Boolean(menuAnchor)}
            onClose={() => setMenuAnchor(null)}
          >
            {nav.showSystemManagement && variant === "client" && (
              <MenuItem
                onClick={() => {
                  setMenuAnchor(null);
                  onOpenSystemManagement?.();
                }}
              >
                System Management
              </MenuItem>
            )}
            <MenuItem
              onClick={() => {
                setMenuAnchor(null);
                onLogout?.();
              }}
            >
              Logout
            </MenuItem>
          </Menu>
        </Grid>
      </Grid>

      {/* The original never rendered a single-button strip: training hid the
          whole bar for non-admins, and the sales rep view had none. */}
      {items.length > 1 && (
        <Box
          sx={{
            bgcolor: tokens.color.railSystemBg,
            borderTop: `5px solid ${tokens.color.surface}`,
            // 40 TOTAL, so 35 of grey beneath the 5px white border.
            //
            // The original was a MUI `<Grid container>` with `height: "40px"`,
            // and MUI's Grid root sets `boxSizing: "border-box"` ITSELF
            // (Grid.js) — it does not depend on CssBaseline, which that app did
            // not have. So the 40 included the border: the strip was 40 tall
            // with 35 of grey, not 45 with 40.
            //
            // Do NOT "correct" this back to 45 from
            // reference/current-ui.dc.html. That recreation draws the strip as
            // a plain <div>, which is content-box, so it renders 5px taller
            // than the app it is recreating. It has been fixed, but it agreed
            // with the wrong value long enough to cost two rounds here.
            //
            // Fixed height, not minHeight: with minHeight the buttons' default
            // 6px vertical padding drives the height instead (measured 41.5).
            height: 40,
            boxSizing: "border-box",
            pl: "10px",
            display: "flex",
            // stretch, as the original Grid container defaulted to: the buttons
            // fill the full 40px and their whole height is clickable.
            alignItems: "stretch",
            overflowX: "auto"
          }}
        >
          {items.map((item, i) => (
            <React.Fragment key={item.id}>
              {i > 0 && (
                <Divider
                  orientation="vertical"
                  variant="middle"
                  flexItem
                  sx={{ borderColor: tokens.color.surface }}
                />
              )}
              <Button
                onClick={() => onSelectItem(item.id)}
                sx={{
                  // `padding: "0px 20px"`, as the original. MUI's default 6px
                  // top/bottom is what made the strip resolve taller than its
                  // content box.
                  px: "20px",
                  py: 0,
                  flex: "none",
                  whiteSpace: "nowrap",
                  color:
                    item.id === selectedItemId
                      ? tokens.color.positive.main
                      : tokens.color.surface
                }}
              >
                {LEGACY_NAV_LABELS[item.id] ?? item.label}
              </Button>
            </React.Fragment>
          ))}
        </Box>
      )}
    </Box>
  );
};

export default LegacyBar;
